// Shop Inventory Adjust dialog helpers (plan_inventory_cost_reporting_2026_10_01
// §C/§D). Pure — mirrors bdr_mobile_app lib/components/inventory/
// return_from_tech_picker.dart (`returnSaveEnabled`) and the page's
// `_fetchTechs` filter, so the two surfaces cannot drift.

import { hasRole } from "./permissions";

/**
 * Adjust reasons with the helper line shown under the picker. Every reason
 * sets stock the same way; the reason only labels the history row — none is
 * an expense and none touches Door Margin (only receives are spend; only
 * issue/return move tech usage). Copy is the mobile copy, verbatim.
 */
export const ADJUST_REASONS = [
  {
    value: "count",
    label: "Physical count",
    helper: "You counted what is on the shelf. Use for first counts and routine stock-takes.",
  },
  {
    value: "correction",
    label: "Correction",
    helper: "Fixes a data mistake: double receive, wrong item scanned, missed issue or return.",
  },
  {
    value: "damage",
    label: "Damaged / unusable",
    helper: "Here but can't be used (spilled, dried out, broken). Removed from stock only — not charged anywhere.",
  },
  { value: "other", label: "Other", helper: "Anything else. Explain in the note." },
];

export const adjustReasonLabel = (value) => ADJUST_REASONS.find((r) => r.value === value)?.label || value || "";
export const adjustReasonHelper = (value) => ADJUST_REASONS.find((r) => r.value === value)?.helper || "";

/** Active refinishers as <select> options, sorted by name. Owner's all-roles account appears on purpose. */
export function refinisherOptions(users) {
  const out = [];
  for (const u of users || []) {
    if (!u || u.active === false) continue;
    if (!hasRole(u.permission, "refinisher")) continue;
    const id = String(u._id || "");
    if (!id) continue;
    const name = `${u.firstName || ""} ${u.lastName || ""}`.trim();
    out.push({ value: id, label: name || id });
  }
  out.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  return out;
}

/** A return can be saved: a tech is chosen and the quantity is a whole number above zero. */
export function returnSaveEnabled(quantity, techId) {
  if (!techId) return false;
  const s = String(quantity ?? "").trim();
  if (!/^\d+$/.test(s)) return false;
  return parseInt(s, 10) > 0;
}

/**
 * Adjust-up guard (§C): the amount an adjust would ADD for a reason that is
 * not a purchase. 0 when no warning applies (return mode, damage, NaN, not up).
 */
export function adjustUpDelta({ reason, newQuantity, currentQuantity }) {
  if (!["count", "correction", "other"].includes(reason)) return 0;
  const next = parseInt(String(newQuantity ?? "").trim(), 10);
  const cur = parseInt(String(currentQuantity ?? "0"), 10) || 0;
  if (!Number.isFinite(next)) return 0;
  return next > cur ? next - cur : 0;
}

/** Guard copy. First count from zero gets the softer line. */
export function adjustUpWarning({ delta, currentQuantity }) {
  if (!delta) return "";
  const cur = parseInt(String(currentQuantity ?? "0"), 10) || 0;
  if (cur === 0) return "First count? That's fine. If these were just bought, use Receive instead so they count as spend.";
  return `Stock goes up by ${delta}. If these were bought, record them as a Receive so they count as spend — a count or correction never does.`;
}
